import { Ban, Loader2 } from 'lucide-react'
import { CONSENT_LABELS, SOURCE_LABELS, SUPPRESSION_LABELS } from '../../../services/crmService'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'

const CONSENT_VARIANT = { opted_in: 'teal', implied: 'blue', none: 'muted' }

function EmptyRow({ children }) {
  return (
    <tr>
      <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">{children}</td>
    </tr>
  )
}

// The contact list body. Each row says whether the address is suppressed and,
// when it isn't, offers to suppress it — the fastest route for "this person
// asked us to stop" without leaving the list to retype their email. Rows can
// also be ticked for a bulk suppress; already-suppressed rows have no box.
//
// `selected` is a Set of contact ids; `onToggle(ids, checked)` flips them.
export default function ContactsTable({ contacts, loading, emptyMessage, selected, onToggle, onSuppress }) {
  const selectable = contacts.filter((c) => !c.suppression_reason)
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.id))
  const someSelected = !allSelected && selectable.some((c) => selected.has(c.id))

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="w-10 px-4 py-2">
              <input
                type="checkbox"
                aria-label="Select all contacts on this page"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected }}
                disabled={loading || selectable.length === 0}
                onChange={(e) => onToggle(selectable.map((c) => c.id), e.target.checked)}
              />
            </th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Sources</th>
            <th className="px-4 py-2 font-medium">Consent</th>
            <th className="px-4 py-2 font-medium">Added</th>
            <th className="px-4 py-2 font-medium"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <EmptyRow><Loader2 className="mx-auto h-5 w-5 animate-spin" /></EmptyRow>
          )}

          {!loading && contacts.length === 0 && <EmptyRow>{emptyMessage}</EmptyRow>}

          {!loading && contacts.map((contact) => (
            <tr key={contact.id} className={`border-t border-border ${selected.has(contact.id) ? 'bg-primary/5' : ''}`}>
              <td className="px-4 py-2">
                {!contact.suppression_reason && (
                  <input
                    type="checkbox"
                    aria-label={`Select ${contact.email}`}
                    checked={selected.has(contact.id)}
                    onChange={(e) => onToggle([contact.id], e.target.checked)}
                  />
                )}
              </td>
              <td className="px-4 py-2 font-medium">{contact.email}</td>
              <td className="px-4 py-2 text-muted-foreground">
                {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—'}
              </td>
              <td className="px-4 py-2">
                <span className="flex flex-wrap gap-1">
                  {(contact.sources ?? []).map((s) => (
                    <Badge key={s} variant="outline">{SOURCE_LABELS[s] ?? s}</Badge>
                  ))}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="flex flex-wrap gap-1">
                  <Badge variant={CONSENT_VARIANT[contact.consent_status] ?? 'muted'}>
                    {CONSENT_LABELS[contact.consent_status] ?? contact.consent_status}
                  </Badge>
                  {contact.suppression_reason && (
                    <Badge variant="orange" title="Excluded from every campaign">
                      <Ban className="mr-1 h-3 w-3" />
                      {SUPPRESSION_LABELS[contact.suppression_reason] ?? 'Suppressed'}
                    </Badge>
                  )}
                </span>
              </td>
              <td className="px-4 py-2 text-muted-foreground">
                {new Date(contact.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-2 text-right">
                {!contact.suppression_reason && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-[var(--orange-11)]"
                    onClick={() => onSuppress(contact)}
                    aria-label={`Suppress ${contact.email}`}
                  >
                    <Ban className="mr-1.5 h-3.5 w-3.5" />
                    Suppress
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
